import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, map, merge, of, switchMap, tap, zip } from 'rxjs';

import { selectCompanyUuid } from '@public-page/store/company';
import { selectStoreUuid } from '@modules/company-page/store/company-stores';
import { selectAppointmentUuid } from '@modules/booking/store/appointment';
import { AuthorizationActions, AuthorizationApiActions, AuthorizationLocalStorageActions } from './auth.actions';
import { selectAuthTokens, selectRefreshTokens } from './auth.selectors';
import { Auth } from '@models';

import { GlobalService, LocalStorageService, ErrorMessageKeyService } from '@modules/core/services';
import { RefreshTokenMapper, SignInMapper, SignUpMapper } from '@modules/core/mappers';
import { LocalStorageKeysEnum, SuccessMessageKeysEnum } from '@modules/core/enums';
import { AuthApiService } from '@paymash/sdk/api/v2';
import { ForgotPasswordMapper } from '@modules/core/mappers/forgot-password.mapper';
import { ResetPasswordMapper } from '@modules/core/mappers';
import { selectQueryParam } from '@public-page/store/router';
import { NotificationActions } from '@public-page/store/notification';
import { GlobalErrorActions } from '@public-page/store/global-error';
import { AuthorizationCustomerActions } from '@modules/authorization/store/customer';

@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private router: Router,
    private globalService: GlobalService,
    private localStorageService: LocalStorageService,
    private signUpMapper: SignUpMapper,
    private signInMapper: SignInMapper,
    private refreshTokenMapper: RefreshTokenMapper,
    private forgotPasswordMapper: ForgotPasswordMapper,
    private resetPasswordMapper: ResetPasswordMapper,
    private authApiService: AuthApiService,
    private errorMessageKeyService: ErrorMessageKeyService
  ) {}

  login = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthorizationActions.login),
      map((action) => this.signInMapper.mapToDTO(action.payload)),
      concatLatestFrom(() => [this.store.select(selectCompanyUuid)]),
      exhaustMap(([payload, companyUuid]) =>
        this.authApiService.signin(companyUuid as string, payload).pipe(
          map((signInResponse) => this.signInMapper.mapToEntity(signInResponse)),
          switchMap((entity) => [
            AuthorizationApiActions.loginSuccess({ auth: entity }),
            NotificationActions.addSuccess({ messageKey: SuccessMessageKeysEnum.LOGIN }),
          ]),
          catchError((error) => {
            const messageKey = this.errorMessageKeyService.getKey(error);
            return merge([AuthorizationApiActions.loginFailure(), NotificationActions.addError({ messageKey })]);
          })
        )
      )
    );
  });

  register$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthorizationActions.register),
      map((action) => this.signUpMapper.mapToDTO(action.payload)),
      concatLatestFrom(() => [this.store.select(selectCompanyUuid)]),
      exhaustMap(([payload, companyUuid]) =>
        this.authApiService.signup(companyUuid as string, payload).pipe(
          switchMap((signupResponse) => {
            const customerEntity = this.signUpMapper.mapToCustomerEntity(signupResponse);
            const accessEntity = this.signUpMapper.mapToAuthEntity(signupResponse);

            return [
              AuthorizationApiActions.registerSuccess({ auth: accessEntity }),
              AuthorizationCustomerActions.loadFromRegisterAPI({ customer: customerEntity }),
            ];
          }),
          catchError((error) => {
            const messageKey = this.errorMessageKeyService.getKey(error);
            return merge([AuthorizationApiActions.registerFailure(), NotificationActions.addError({ messageKey })]);
          })
        )
      )
    );
  });

  loadTokensFromLocalStorage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthorizationLocalStorageActions.loadAuthToken),
      switchMap(() => {
        return of(this.localStorageService.getData<Auth>(LocalStorageKeysEnum.authState));
      }),
      map((auth) => AuthorizationLocalStorageActions.setAuthToken({ auth: auth }))
    );
  });

  // todo: refactor to new realization
  updateTokensFromLocalStorage$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          AuthorizationApiActions.refreshAuthTokenSuccess,
          AuthorizationApiActions.registerSuccess,
          AuthorizationApiActions.loginSuccess
        ),
        switchMap(() =>
          this.store.select(selectAuthTokens).pipe(
            tap((auth) => {
              this.localStorageService.setData(LocalStorageKeysEnum.authState, auth);
            })
          )
        )
      );
    },
    { dispatch: false }
  );

  // todo: refactor to new realization
  refreshToken$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthorizationActions.refreshAuthToken),
      switchMap(() =>
        zip(this.store.select(selectCompanyUuid), this.store.select(selectRefreshTokens)).pipe(
          exhaustMap(([companyUuid, refreshToken]) => {
            const payload = this.refreshTokenMapper.mapToDTO({ refreshToken: refreshToken as string });
            return this.authApiService.refreshToken(companyUuid, payload);
          }),
          map((data) => {
            const entity = this.refreshTokenMapper.mapToEntity(data);
            return AuthorizationApiActions.refreshAuthTokenSuccess({ auth: entity });
          }),
          catchError(() => of(AuthorizationApiActions.refreshAuthTokenFailure))
        )
      )
    );
  });

  // todo: refactor to new realization
  redirectToLogin$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthorizationActions.redirectToLogin),
        switchMap(() =>
          zip(
            this.store.select(selectCompanyUuid),
            this.store.select(selectStoreUuid),
            this.store.select(selectAppointmentUuid)
          ).pipe(
            tap(([companyUuid, storeUuid, appointmentUuid]) => {
              const path = this.globalService.hasCustomDomain
                ? ['booking', 'confirm']
                : [companyUuid, 'booking', 'confirm'];

              this.router.navigate(path, {
                queryParams: { storeUuid, appointmentUuid },
              });
            })
          )
        )
      );
    },
    { dispatch: false }
  );

  // todo: refactor to new realization
  redirectToForgotPassword$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthorizationActions.redirectToForgotPassword),
        switchMap(() =>
          zip(
            this.store.select(selectCompanyUuid),
            this.store.select(selectStoreUuid),
            this.store.select(selectAppointmentUuid)
          ).pipe(
            tap(([companyUuid, storeUuid, appointmentUuid]) => {
              const path = this.globalService.hasCustomDomain ? ['forgot-password'] : [companyUuid, 'forgot-password'];

              this.router.navigate(path, {
                queryParams: { storeUuid, appointmentUuid },
              });
            })
          )
        )
      );
    },
    { dispatch: false }
  );

  // todo: refactor to new realization
  redirectToRegister$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthorizationActions.redirectToRegister),
        switchMap(() =>
          zip(
            this.store.select(selectCompanyUuid),
            this.store.select(selectStoreUuid),
            this.store.select(selectAppointmentUuid)
          ).pipe(
            tap(([companyUuid, storeUuid, appointmentUuid]) => {
              const path = this.globalService.hasCustomDomain
                ? ['booking', 'confirm', 'register']
                : [companyUuid, 'booking', 'confirm', 'register'];

              this.router.navigate(path, {
                queryParams: { storeUuid, appointmentUuid },
                replaceUrl: true,
              });
            })
          )
        )
      );
    },
    { dispatch: false }
  );

  // todo: refactor to new realization
  redirectToGuestForm$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthorizationActions.redirectToGuest),
        switchMap(() =>
          zip(
            this.store.select(selectCompanyUuid),
            this.store.select(selectStoreUuid),
            this.store.select(selectAppointmentUuid)
          ).pipe(
            tap(([companyUuid, storeUuid, appointmentUuid]) => {
              const path = this.globalService.hasCustomDomain
                ? ['booking', 'confirm', 'guest']
                : [companyUuid, 'booking', 'confirm', 'guest'];

              this.router.navigate(path, {
                queryParams: { storeUuid, appointmentUuid },
                replaceUrl: true,
              });
            })
          )
        )
      );
    },
    { dispatch: false }
  );

  // todo: refactor to new realization
  redirectToAuthorizedForm$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthorizationApiActions.loginSuccess),
        switchMap(() =>
          zip(
            this.store.select(selectCompanyUuid),
            this.store.select(selectStoreUuid),
            this.store.select(selectAppointmentUuid)
          ).pipe(
            tap(([companyUuid, storeUuid, appointmentUuid]) => {
              const path = this.globalService.hasCustomDomain
                ? ['booking', 'confirm', 'authorized']
                : [companyUuid, 'booking', 'confirm', 'authorized'];

              this.router.navigate(path, {
                queryParams: { storeUuid, appointmentUuid },
                replaceUrl: true,
              });
            })
          )
        )
      );
    },
    { dispatch: false }
  );

  showForgotPasswordSuccessInfo$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthorizationActions.resetPassword),
      map((action) => this.forgotPasswordMapper.mapToDTO(action.payload)),
      concatLatestFrom(() => [this.store.select(selectCompanyUuid)]),
      exhaustMap(([payload, companyUuid]) =>
        this.authApiService.initResetPassword(companyUuid, payload).pipe(
          map(() => AuthorizationApiActions.resetPasswordSuccess()),
          catchError((error) => {
            const messageKey = this.errorMessageKeyService.getKey(error);
            return merge([
              AuthorizationApiActions.resetPasswordFailure(),
              NotificationActions.addError({ messageKey }),
            ]);
          })
        )
      )
    );
  });

  confirmNewPassword$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthorizationActions.confirmNewPassword),
      map((action) => this.resetPasswordMapper.mapToDTO(action.payload)),
      concatLatestFrom(() => [this.store.select(selectCompanyUuid), this.store.select(selectQueryParam('token'))]),
      exhaustMap(([payload, companyUuid, token]) =>
        this.authApiService.updatePassword(companyUuid, token as string, payload).pipe(
          map(() => AuthorizationApiActions.confirmNewPasswordSuccess()),
          catchError((error) => {
            const messageKey = this.errorMessageKeyService.getKey(error);
            return merge([
              AuthorizationApiActions.confirmNewPasswordFailure(),
              NotificationActions.addError({ messageKey }),
            ]);
          })
        )
      )
    );
  });

  // todo: refactor to new realization
  redirectToPublicPage$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthorizationApiActions.confirmNewPasswordSuccess, GlobalErrorActions.redirectToMainPage),
        switchMap(() =>
          zip(this.store.select(selectCompanyUuid)).pipe(
            tap(([companyUuid]) => {
              const path = this.globalService.hasCustomDomain ? ['public'] : [companyUuid, 'public'];

              this.router.navigate(path);
            })
          )
        )
      );
    },
    { dispatch: false }
  );

  verifyResetPassword$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthorizationActions.verifyResetPassword),
        concatLatestFrom(() => [this.store.select(selectCompanyUuid), this.store.select(selectQueryParam('token'))]),
        exhaustMap(([, companyUuid, token]) =>
          this.authApiService.verifyResetPassword(companyUuid, token as string).pipe(
            map(() => AuthorizationApiActions.verifyResetPasswordSuccess()),
            catchError((error) =>
              merge([AuthorizationApiActions.verifyResetPasswordFailure(), GlobalErrorActions.handleError({ error })])
            )
          )
        )
      );
    },
    { dispatch: false }
  );
}
